import { FrameRange, VideoCreativeLayer } from 'content-creation-common/models';
import { fullyQualifyURL } from 'content-creation-common/utils/url';
import { media as mediaConstants } from 'content-creation-common/constants';

import VideoFile from './models/VideoFile';
import {
  shiftFrameRangesBeforeFrameToTail,
  truncateFrameRanges,
} from '../../util/frameRange';
import { isNotNumber } from '../../util/numbers';
import { isEmptyString, isNil } from '../../util/object';
import {
  createVRPublicMediaVariantUrl,
  getVRPublicThumbnailUrl,
} from '../video-reformatting/util/media';
import { createMediaVariantUrl } from '../../util/mediaUtils';
import {
  CHROME_VERSION_WITH_WEBM_EXTRACTION_FIX,
  isChromeVersionAtLeast,
} from '../../util/browser';
import { DEV, MEDIA_TYPE, PLAYBACK_FRAMES_PER_SECOND } from '../../constants';
import {
  DEFAULT_CHUNK_VARIANT,
  DEFAULT_MAX_FETCH_FRAMES_REQUESTS,
  VIDEO_TRANSFORM_TYPE,
} from '../media/constants';
import { CHUNK_INDEX_TOKEN, NUM_FRAMES_TO_BUFFER } from './constants';
const { MEDIA_PROCESSORS, WEBM } = mediaConstants;

/**
 * given a list of frame ranges and frame index, determine a new list of frame ranges
 * starting at the give frame index and up to number of frames to buffer
 * @param {array<FrameRange>} playableFrameRanges - unordered list of frame ranges to prioritize
 * @param {number} frameIndex - frame to prioritize
 * @param {number} framesToBuffer - max number of frames to return
 * @return {array<FrameRange>} prioritized list of frame ranges
 */
export const getPrioritizedFrameRanges = (
  playableFrameRanges,
  frameIndex = 0,
  framesToBuffer = NUM_FRAMES_TO_BUFFER,
) => {
  const nextFrameRanges = shiftFrameRangesBeforeFrameToTail(
    playableFrameRanges,
    frameIndex,
  );

  return truncateFrameRanges(nextFrameRanges, framesToBuffer);
};

/**
 * given a list of frame ranges, order the list in place based on a given frame index
 * @param {array<FrameRange>} frameRanges - unordered list of frame ranges
 * @param {number} frameIndex - frame to start the sorting
 * @return {array<FrameRange>} ordered list of frame ranges by the frame index
 */
export const orderFrameRangesInPlace = (frameRanges, frameIndex = 0) => {
  if (frameRanges.length > 0) {
    for (let i = 0; i < frameRanges.length; i++) {
      const frameRange = frameRanges[i];
      if (
        frameRange.start !== frameIndex &&
        frameRange.containsFrameNumber(frameIndex)
      ) {
        const frameRange1 = new FrameRange(frameIndex, frameRange.end);
        const frameRange2 = new FrameRange(frameRange.start, frameIndex - 1);
        frameRanges.splice(i, 1, frameRange1, frameRange2);
        break;
      }
    }

    frameRanges.sort((frameRangeA, frameRangeB) => {
      const distanceFromA = frameRangeA.start - frameIndex;
      const distanceFromB = frameRangeB.start - frameIndex;
      if (distanceFromA < 0) {
        if (distanceFromB > 0) return 1;
        // If rangeB distance is more negative than rangeA distance then rank rangeB higher
        return distanceFromB < distanceFromA ? -1 : 1;
      }
      return distanceFromB < 0 ? -1 : distanceFromA - distanceFromB;
    });
  }
};

export const getUnavailableFramesNeededForPlayback = (
  frameIndex,
  playableFrameRanges,
  videoFrameCache,
  checkRequestedFrames = false,
) => {
  const prioritizedFrameRanges = getPrioritizedFrameRanges(
    playableFrameRanges,
    frameIndex,
  );
  const unavailableFrameRanges = videoFrameCache.getUnavailableFrameRanges(
    prioritizedFrameRanges,
    checkRequestedFrames,
  );

  orderFrameRangesInPlace(unavailableFrameRanges, frameIndex);
  return unavailableFrameRanges;
};

// todo() take into account of media's processor
export const shouldUsePngSequence = (medium) => {
  if (isNil(medium)) return false;
  const { mediaType, mimeType, serverId, usePngSequence } = medium;
  const isMediaWebM = mediaType === MEDIA_TYPE.VIDEO && mimeType === WEBM;
  const isChromeVersionWithWebMFix = isChromeVersionAtLeast(
    CHROME_VERSION_WITH_WEBM_EXTRACTION_FIX,
  );
  return (
    !isEmptyString(serverId) &&
    (usePngSequence || (isMediaWebM && !isChromeVersionWithWebMFix))
  );
};

const getFrameChunkSize = (videoCreativeLayer) => {
  const { media, processors } = videoCreativeLayer;

  // default Broadway chunnk size
  if (processors.has(MEDIA_PROCESSORS.VIS)) {
    return DEFAULT_CHUNK_VARIANT.chunkSize;
  }

  // png sequence batch 10 frames requests in parallel
  if (shouldUsePngSequence(media)) {
    return DEFAULT_MAX_FETCH_FRAMES_REQUESTS;
  }

  // default chunk is 1 frame per chunk (number of frames to batch during extraction)
  return 1;
};

export const getVideoFile = (mediaIdentifier, videoCreativeLayer) => {
  if (!VideoCreativeLayer.isVideoCreativeLayer(videoCreativeLayer)) return null;

  const { media, mediaURL, processors } = videoCreativeLayer;
  if (isNil(media)) return null;

  const { duration, height, serverId, templateId, width } = media;

  if (isNotNumber(duration) || isNotNumber(height) || isNotNumber(width)) {
    return null;
  }

  const totalFrames = Math.floor(media.duration * PLAYBACK_FRAMES_PER_SECOND);

  const isProcessedByVIS = processors.has(MEDIA_PROCESSORS.VIS);
  // TODO(omegdadi): We can hardcode this for now, but in the future we should pull
  // the base variant data from the media's variants.
  const chunkVariant = {
    chunkIndex: CHUNK_INDEX_TOKEN,
    ...DEFAULT_CHUNK_VARIANT,
  };

  const framesPerChunk = getFrameChunkSize(videoCreativeLayer);
  const videoFile = new VideoFile(
    mediaIdentifier,
    framesPerChunk,
    height,
    totalFrames,
    mediaURL,
    width,
    serverId,
  );
  if (isProcessedByVIS) {
    videoFile.url = fullyQualifyURL(
      DEV
        ? createVRPublicMediaVariantUrl()
        : createMediaVariantUrl(
            serverId,
            VIDEO_TRANSFORM_TYPE.CHUNK,
            chunkVariant,
          ),
    );

    videoFile.fetchFrameCallback = DEV ? getVRPublicThumbnailUrl : null;
  }

  if (!isEmptyString(templateId)) {
    videoFile.templateId = templateId;
  }

  return videoFile;
};
